import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

/**
 * ConciergeAgent floating chat widget — visible on every AD page.
 * Hits POST /api/agents/concierge/chat. Maintains a single session per visit.
 */
export default function ConciergeWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hi — I'm your Concierge. Ask me about open searches, candidates, or next steps." },
  ]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setDraft("");
    setBusy(true);
    try {
      const { data } = await api.post("/agents/concierge/chat", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "Concierge is offline. Please retry in a moment." }]);
    } finally { setBusy(false); }
  };

  return createPortal(
    <>
      <button
        data-testid="concierge-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        className="rounded-full bg-blaze text-white shadow-2xl shadow-blaze/40 flex items-center justify-center hover:glow-blaze transition-all"
        style={{
          position: "fixed",
          right: "16px",
          bottom: "calc(20px + env(safe-area-inset-bottom))",
          height: "56px",
          width: "56px",
          zIndex: 60,
        }}
        aria-label="Open Concierge"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed z-[55] bottom-24 right-3 left-3 md:left-auto md:right-6 md:bottom-24 md:w-[380px] max-h-[70vh] flex flex-col bg-appcard border border-neutral-800 shadow-2xl"
        >
          <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between bg-neutral-950">
            <div className="flex items-center gap-2">
              <Sparkles className="text-volt" size={16} />
              <span className="heading text-base">CONCIERGE AGENT</span>
            </div>
            <span className="text-[10px] tracking-widest text-neutral-500">CLAUDE 4.5</span>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="concierge-messages">
            {msgs.map((m, i) => (
              <div
                key={i}
                data-testid={`concierge-msg-${m.role}-${i}`}
                className={`max-w-[85%] px-3 py-2 text-sm leading-snug ${
                  m.role === "user"
                    ? "ml-auto bg-blaze text-white"
                    : "mr-auto bg-neutral-900 text-neutral-100 border border-neutral-800"
                }`}
              >
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="mr-auto bg-neutral-900 border border-neutral-800 text-xs text-neutral-400 px-3 py-2">Thinking…</div>
            )}
            <div ref={endRef} />
          </div>

          <div className="border-t border-neutral-800 p-2 flex gap-2 bg-neutral-950">
            <input
              data-testid="concierge-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about your pipeline…"
              className="flex-1 bg-appcard border border-neutral-800 px-3 py-2 text-sm text-white outline-none focus:border-blaze min-h-[44px]"
            />
            <button
              data-testid="concierge-send-btn"
              onClick={send}
              disabled={busy || !draft.trim()}
              className="bg-blaze text-white px-4 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
